import { useCallback, useEffect, useMemo, useState } from "react";
import { captureError } from "@/lib/sentry";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  LightningIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  TrophyIcon,
  UsersThreeIcon,
  GlobeIcon,
  PaletteIcon,
  TranslateIcon,
} from "@phosphor-icons/react";
import {
  StepLeagues,
  StepTeams,
  StepPlayers,
  StepStyle,
  StepLanguage,
} from "@/components/onboarding/Steps";

const STEPS = [
  { id: 1, label: "Leagues", icon: GlobeIcon },
  { id: 2, label: "Teams", icon: TrophyIcon },
  { id: 3, label: "Players", icon: UsersThreeIcon },
  { id: 4, label: "Style", icon: PaletteIcon },
  { id: 5, label: "Language", icon: TranslateIcon },
];

function toggleArr(arr, v) {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}

export default function Onboarding() {
  const { user, setUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [leagues, setLeagues] = useState(user?.favorite_leagues || []);
  const [teamKeys, setTeamKeys] = useState(user?.favorite_teams || []);
  const [playerIds, setPlayerIds] = useState(user?.favorite_players || []);
  const [style, setStyle] = useState(user?.content_style || "casual_fan");
  const [language, setLanguage] = useState(user?.language_preference || "en");
  const [allTeams, setAllTeams] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadCatalog = useCallback(async () => {
    try {
      const [t, p] = await Promise.all([api.get("/teams"), api.get("/players")]);
      setAllTeams(t.data || []);
      setAllPlayers(p.data || []);
    } catch (err) {
      captureError("onboarding catalog load failed:", err);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const filteredTeams = useMemo(
    () => allTeams.filter((t) => leagues.length === 0 || leagues.includes(t.league)),
    [allTeams, leagues],
  );
  const filteredPlayers = useMemo(
    () => allPlayers.filter((p) => leagues.length === 0 || leagues.includes(p.league)),
    [allPlayers, leagues],
  );

  const next = useCallback(() => setStep((s) => Math.min(s + 1, STEPS.length)), []);
  const back = useCallback(() => setStep((s) => Math.max(s - 1, 1)), []);

  const finish = async () => {
    setSaving(true);
    try {
      const res = await api.put("/auth/preferences", {
        favorite_leagues: leagues,
        favorite_teams: teamKeys,
        favorite_players: playerIds,
        content_style: style,
        language_preference: language,
        onboarded: true,
      });
      setUser(res.data);
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      captureError("onboarding finish failed:", err);
    } finally {
      setSaving(false);
    }
  };

  const skip = async () => {
    try {
      await api.put("/auth/preferences", { onboarded: true });
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      captureError("onboarding skip failed:", err);
    }
  };

  const canAdvance = {
    1: leagues.length > 0,
    2: true,
    3: true,
    4: !!style,
    5: !!language,
  }[step];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-2">
          <LightningIcon weight="fill" className="text-[#0A84FF]" size={24} />
          <span className="font-display font-bold text-xl tracking-tight">Gamly AI</span>
          <span className="ml-auto text-xs font-bold uppercase tracking-[0.2em] text-white/40">My Fan DNA</span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <Stepper step={step} onJump={setStep} />

        {step === 1 && (
          <StepLeagues value={leagues} onToggle={(v) => setLeagues((arr) => toggleArr(arr, v))} />
        )}
        {step === 2 && (
          <StepTeams teams={filteredTeams} value={teamKeys} onToggle={(v) => setTeamKeys((arr) => toggleArr(arr, v))} />
        )}
        {step === 3 && (
          <StepPlayers players={filteredPlayers} value={playerIds} onToggle={(v) => setPlayerIds((arr) => toggleArr(arr, v))} />
        )}
        {step === 4 && <StepStyle value={style} onChange={setStyle} />}
        {step === 5 && <StepLanguage value={language} onChange={setLanguage} />}

        <NavButtons
          step={step}
          totalSteps={STEPS.length}
          canAdvance={canAdvance}
          saving={saving}
          onBack={back}
          onNext={next}
          onFinish={finish}
        />

        <button onClick={skip} data-testid="onb-skip" className="block mx-auto mt-6 text-[11px] font-bold uppercase tracking-wider text-white/40 hover:text-white">
          Skip for now
        </button>
      </div>
    </div>
  );
}

function Stepper({ step, onJump }) {
  return (
    <ol className="flex items-center justify-between gap-1 sm:gap-3 mb-8" data-testid="onboarding-stepper">
      {STEPS.map((s, i) => {
        const active = step === s.id;
        const done = step > s.id;
        return (
          <li key={s.id} className="flex-1 flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => done && onJump(s.id)}
              data-testid={`step-${s.id}`}
              className={`shrink-0 w-9 h-9 rounded-md flex items-center justify-center border ${
                active
                  ? "bg-[#0A84FF] border-[#0A84FF] text-white"
                  : done
                  ? "bg-[#32D74B]/10 border-[#32D74B] text-[#32D74B]"
                  : "border-white/15 text-white/40"
              }`}
            >
              {done ? <CheckCircleIcon weight="fill" /> : <s.icon weight="bold" />}
            </button>
            <span className={`hidden sm:inline text-[11px] font-bold uppercase tracking-wider ${active ? "text-white" : "text-white/40"}`}>
              {s.label}
            </span>
            {i < STEPS.length - 1 && <span className="flex-1 h-px bg-white/10" />}
          </li>
        );
      })}
    </ol>
  );
}

function NavButtons({ step, totalSteps, canAdvance, saving, onBack, onNext, onFinish }) {
  return (
    <div className="flex items-center justify-between mt-8">
      <button
        type="button"
        onClick={onBack}
        disabled={step === 1}
        data-testid="onb-back"
        className="inline-flex items-center gap-1 px-4 py-2 rounded-md border border-white/15 text-white/80 text-xs font-bold uppercase tracking-wider hover:bg-white/5 disabled:opacity-40"
      >
        <ArrowLeftIcon /> Back
      </button>
      {step < totalSteps ? (
        <button
          type="button"
          onClick={onNext}
          disabled={!canAdvance}
          data-testid="onb-next"
          className="inline-flex items-center gap-1 px-5 py-2 rounded-md bg-[#0A84FF] hover:bg-blue-600 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-40"
        >
          Next <ArrowRightIcon />
        </button>
      ) : (
        <button
          type="button"
          onClick={onFinish}
          disabled={saving}
          data-testid="onb-finish"
          className="inline-flex items-center gap-2 px-6 py-2 rounded-md bg-[#32D74B] hover:bg-green-500 text-black text-xs font-black uppercase tracking-wider disabled:opacity-40"
        >
          <LightningIcon weight="fill" /> {saving ? "Setting up…" : "Lock in my Fan DNA"}
        </button>
      )}
    </div>
  );
}
